/**
 * Hand-drawn SVG art for the eight concept mini-sites: logos, hero illustrations,
 * ornaments and product tiles, each in its shop's own palette. No photos, no imagery from
 * elsewhere: every shape is drawn here. All of it is decorative (`aria-hidden`); the
 * gallery card names each site for assistive tech.
 *
 * `uid` keeps SVG `<pattern>` ids unique, since every site renders twice (browser and phone).
 *
 * @module
 */
import type { Key, ReactNode } from 'react'
import type { ShowcaseProjectId } from '@/content/site'

type Art = (props: { uid: string }) => ReactNode

/** One shop's art kit. `theme` picks its CSS art direction. */
export interface SiteArt {
  readonly theme: string
  readonly Logo: Art
  readonly Ornament: Art
  readonly Hero: Art
  readonly Products: readonly [Art, Art, Art]
}

const svg = (viewBox: string, children: ReactNode, className?: string) => (
  <svg
    viewBox={viewBox}
    className={className}
    aria-hidden="true"
    focusable="false"
    preserveAspectRatio="xMidYMid meet"
  >
    {children}
  </svg>
)

const ring = (count: number, fn: (index: number, angle: number) => ReactNode) =>
  Array.from({ length: count }, (_, i) => fn(i, (i / count) * 360))

// ---------------------------------------------------------------------------------------
// Sharma Sweets: prism lilac, pearl and maroon; festive toran and mehrab arch
// ---------------------------------------------------------------------------------------

const SH = {
  cream: '#fafafc',
  saffron: '#5b57f2',
  deep: '#5b57f2',
  maroon: '#7d1d2c',
  gold: '#5b57f2',
  pista: '#5b57f2',
  silver: '#fff1d6',
}

const diamond = ({ key, x, y, s = 1 }: { key?: Key; x: number; y: number; s?: number }) => (
  <path
    key={key}
    d={`M${x} ${y}l${9 * s} ${-6 * s}l${9 * s} ${6 * s}l${-9 * s} ${6 * s}Z`}
    fill={SH.silver}
    stroke="#bdb6a6"
    strokeWidth="0.7"
  />
)

const laddoo = ({ key, x, y, r }: { key?: Key; x: number; y: number; r: number }) => (
  <g key={key}>
    <circle cx={x} cy={y} r={r} fill={SH.saffron} />
    <circle cx={x - r * 0.35} cy={y - r * 0.35} r={r * 0.32} fill="#e9ddff" opacity="0.8" />
    {ring(9, (i, a) => (
      <circle
        key={i}
        cx={x + Math.cos((a * Math.PI) / 180) * r * 0.62}
        cy={y + Math.sin((a * Math.PI) / 180) * r * 0.62}
        r={r * 0.09}
        fill={SH.deep}
      />
    ))}
  </g>
)

const sharma: SiteArt = {
  theme: 'sharma',
  Logo: () =>
    svg(
      '0 0 24 24',
      <g fill={SH.maroon}>
        <path d="M12 21C8.5 16.8 8.8 10 12 4.5c3.2 5.5 3.5 12.3 0 16.5Z" />
        <path d="M11 21C6.5 19.5 3.6 15 3.5 10c4 1.2 7 5 7.5 11Z" opacity="0.75" />
        <path d="M13 21c4.5-1.5 7.4-6 7.5-11-4 1.2-7 5-7.5 11Z" opacity="0.75" />
      </g>,
    ),
  Ornament: () =>
    svg(
      '0 0 400 46',
      <>
        <path
          d="M0 6Q25 26 50 6T100 6T150 6T200 6T250 6T300 6T350 6T400 6"
          fill="none"
          stroke={SH.deep}
          strokeWidth="1.6"
        />
        {Array.from({ length: 8 }, (_, i) => {
          const x = 25 + i * 50
          return (
            <g key={i}>
              <path d={`M${x + 25} 6q-6 14 0 26q6-12 0-26Z`} fill={SH.pista} />
              <circle cx={x} cy={17} r={8} fill={i % 2 ? SH.deep : SH.saffron} />
              <circle cx={x} cy={17} r={3.6} fill={i % 2 ? SH.saffron : SH.deep} />
              <circle cx={x} cy={31} r={4.5} fill={SH.saffron} />
            </g>
          )
        })}
      </>,
    ),
  Hero: () =>
    svg(
      '0 0 200 220',
      <>
        <path
          d="M18 220V102C18 62 58 36 100 16c42 20 82 46 82 86v118Z"
          fill="#fff1d6"
          stroke={SH.maroon}
          strokeWidth="3"
        />
        <path
          d="M30 220V104c0-33 34-55 70-73 36 18 70 40 70 73v116"
          fill="none"
          stroke={SH.gold}
          strokeWidth="1.4"
          strokeDasharray="1 4"
          strokeLinecap="round"
        />
        <circle cx="100" cy="72" r="13" fill="none" stroke={SH.gold} strokeWidth="1.4" />
        <circle cx="100" cy="72" r="5" fill={SH.deep} />
        <ellipse cx="100" cy="184" rx="66" ry="17" fill={SH.gold} />
        <ellipse cx="100" cy="181" rx="57" ry="12.5" fill="#e9ddff" />
        {[64, 82, 100, 118].map((x) => diamond({ key: x, x: x - 9, y: 172 }))}
        {[73, 91, 109].map((x) => diamond({ key: x, x: x - 9, y: 160 }))}
        {[82, 100].map((x) => diamond({ key: x, x: x - 9, y: 148 }))}
        {diamond({ x: 82, y: 136 })}
        {laddoo({ x: 140, y: 164, r: 11 })}
        {laddoo({ x: 153, y: 174, r: 9.5 })}
        {laddoo({ x: 128, y: 176, r: 9.5 })}
      </>,
    ),
  Products: [
    () =>
      svg(
        '0 0 100 100',
        <>
          <path d="M52 40l26 17-26 17-26-17Z" fill="#fff1d6" />
          <path d="M48 26l28 18-28 18-28-18Z" fill={SH.silver} stroke="#bdb6a6" />
          <path d="M34 40l14-9 14 9" fill="none" stroke="#fff" strokeWidth="1.6" opacity="0.8" />
        </>,
      ),
    () => svg('0 0 100 100', laddoo({ x: 50, y: 52, r: 27 })),
    () =>
      svg(
        '0 0 100 100',
        <>
          <path d="M50 20l30 14-30 14-30-14Z" fill="#fff1d6" />
          <path d="M20 34l30 14v32L20 66Z" fill="#e9ddff" />
          <path d="M80 34 50 48v32l30-14Z" fill="#cfcbff" />
          {[54, 60, 66, 72].map((y) => (
            <path
              key={y}
              d={`M22 ${y - 12}l26 12M78 ${y - 12}l-26 12`}
              stroke="#5b57f2"
              strokeWidth="0.8"
            />
          ))}
        </>,
      ),
  ],
}

// ---------------------------------------------------------------------------------------
// Noor Chikan: ivory and rose, chikankari threadwork
// ---------------------------------------------------------------------------------------

const NO = { ivory: '#fafafc', rose: '#cf8e99', deep: '#8c3d50', blush: '#f4dedf', sage: '#9faa8c' }

const buti = ({
  key,
  x,
  y,
  r = 0,
  s = 1,
}: {
  key?: Key
  x: number
  y: number
  r?: number
  s?: number
}) => (
  <g key={key} transform={`translate(${x} ${y}) rotate(${r}) scale(${s})`}>
    <path
      d="M0 0C5-8 15-7 15 3c0 8-7 12-12 10-4-2-4.5-7-1-8 2.5-.7 4 1.5 3 3"
      fill="none"
      stroke={NO.deep}
      strokeWidth="0.9"
      strokeLinecap="round"
    />
    <circle cx="9" cy="2" r="1" fill={NO.rose} />
  </g>
)

const murri = ({ key, x, y }: { key?: Key; x: number; y: number }) => (
  <g key={key} fill={NO.rose}>
    <circle cx={x} cy={y} r="1.3" />
    <circle cx={x + 3.2} cy={y + 1.2} r="1.1" />
    <circle cx={x + 0.6} cy={y + 3.4} r="1.1" />
  </g>
)

const noor: SiteArt = {
  theme: 'noor',
  Logo: () => svg('0 0 24 24', buti({ x: 4, y: 11, s: 1.05 })),
  Ornament: () =>
    svg(
      '0 0 200 60',
      <>
        <path
          d="M-4 44C26 12 56 62 98 32s70-26 106 4"
          fill="none"
          stroke={NO.rose}
          strokeWidth="0.8"
        />
        {buti({ x: 30, y: 30, r: -30, s: 0.8 })}
        {buti({ x: 82, y: 36, r: 20, s: 0.7 })}
        {buti({ x: 140, y: 16, r: -10, s: 0.8 })}
        {murri({ x: 58, y: 46 })}
        {murri({ x: 116, y: 30 })}
        {murri({ x: 178, y: 34 })}
      </>,
    ),
  Hero: () =>
    svg(
      '0 0 200 200',
      <g fill="none" strokeLinecap="round">
        <circle
          cx="100"
          cy="100"
          r="88"
          stroke={NO.rose}
          strokeWidth="2.2"
          strokeDasharray="0.1 5.2"
        />
        <circle cx="100" cy="100" r="80" stroke={NO.rose} strokeWidth="0.6" />
        {ring(16, (i, a) => (
          <ellipse
            key={i}
            cx="100"
            cy="47"
            rx="9"
            ry="27"
            stroke={NO.deep}
            strokeWidth="0.9"
            transform={`rotate(${a} 100 100)`}
          />
        ))}
        {ring(16, (i, a) => (
          <circle
            key={i}
            cx="100"
            cy="24"
            r="1.6"
            fill={NO.rose}
            stroke="none"
            transform={`rotate(${a + 11.25} 100 100)`}
          />
        ))}
        {ring(8, (i, a) => (
          <ellipse
            key={i}
            cx="100"
            cy="74"
            rx="7"
            ry="15"
            stroke={NO.rose}
            strokeWidth="0.9"
            transform={`rotate(${a + 22.5} 100 100)`}
          />
        ))}
        <circle cx="100" cy="100" r="11" stroke={NO.deep} strokeWidth="0.9" />
        {ring(10, (i, a) => (
          <circle
            key={i}
            cx="100"
            cy="93"
            r="1.2"
            fill={NO.deep}
            stroke="none"
            transform={`rotate(${a} 100 100)`}
          />
        ))}
      </g>,
    ),
  Products: [
    () =>
      svg(
        '0 0 100 100',
        <>
          <path
            d="M35 18l9-4q6 6 12 0l9 4 15 12-8 10-6-6v54H34V34l-6 6-8-10Z"
            fill="#fafafc"
            stroke={NO.deep}
            strokeWidth="1.2"
            strokeLinejoin="round"
          />
          <path
            d="M42 22q8 12 16 0"
            fill="none"
            stroke={NO.rose}
            strokeWidth="1.8"
            strokeDasharray="0.1 3"
            strokeLinecap="round"
          />
          {buti({ x: 44, y: 52, s: 0.6 })}
          {murri({ x: 52, y: 70 })}
        </>,
      ),
    () =>
      svg(
        '0 0 100 100',
        <>
          <path
            d="M18 22c22 12 42 12 64 0l-5 62c-18-9-36-9-54 0Z"
            fill={NO.blush}
            stroke={NO.rose}
            strokeWidth="1.1"
          />
          <path
            d="M24 78c17-7 35-7 52 0"
            fill="none"
            stroke={NO.deep}
            strokeWidth="1.8"
            strokeDasharray="0.1 3.2"
            strokeLinecap="round"
          />
          {buti({ x: 40, y: 44, s: 0.7 })}
        </>,
      ),
    () =>
      svg(
        '0 0 100 100',
        <>
          <path
            d="M42 14h16l2 20 22 54H18l22-54Z"
            fill="#fafafc"
            stroke={NO.deep}
            strokeWidth="1.2"
            strokeLinejoin="round"
          />
          <path d="M40 34h20" stroke={NO.rose} strokeWidth="1.4" />
          <path
            d="M22 84c19-5 37-5 56 0"
            fill="none"
            stroke={NO.rose}
            strokeWidth="2"
            strokeDasharray="0.1 3.4"
            strokeLinecap="round"
          />
          {murri({ x: 47, y: 52 })}
        </>,
      ),
  ],
}

// ---------------------------------------------------------------------------------------
// Raipur Hardware Co.: safety yellow and black, an industrial grid
// ---------------------------------------------------------------------------------------

const RH = { yellow: '#5b57f2', black: '#0f1222', steel: '#8c9096', concrete: '#fff1d6' }

const hexagon = (cx: number, cy: number, r: number) =>
  ring(6, (_, a) => {
    const t = (a * Math.PI) / 180
    return `${(cx + Math.cos(t) * r).toFixed(1)},${(cy + Math.sin(t) * r).toFixed(1)}`
  }).join(' ')

const raipur: SiteArt = {
  theme: 'raipur',
  Logo: () =>
    svg(
      '0 0 24 24',
      <>
        <polygon points={hexagon(12, 12, 10)} fill={RH.yellow} />
        <circle cx="12" cy="12" r="4" fill={RH.black} />
      </>,
    ),
  Ornament: ({ uid }) =>
    svg(
      '0 0 400 16',
      <>
        <defs>
          <pattern
            id={`${uid}hz`}
            width="14"
            height="14"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <rect width="7" height="14" fill={RH.black} />
          </pattern>
        </defs>
        <rect width="400" height="16" fill={RH.yellow} />
        <rect width="400" height="16" fill={`url(#${uid}hz)`} />
      </>,
    ),
  Hero: () =>
    svg(
      '0 0 200 200',
      <>
        <polygon points={hexagon(108, 98, 72)} fill={RH.black} />
        <polygon points={hexagon(108, 98, 60)} fill={RH.yellow} />
        <circle cx="108" cy="98" r="31" fill={RH.black} />
        <circle
          cx="108"
          cy="98"
          r="24"
          fill="none"
          stroke={RH.steel}
          strokeWidth="2"
          strokeDasharray="3 3"
        />
        <g transform="rotate(-38 70 150)">
          <rect
            x="14"
            y="140"
            width="112"
            height="20"
            rx="10"
            fill={RH.steel}
            stroke={RH.black}
            strokeWidth="3"
          />
          <path
            d="M120 128a24 24 0 1 1 0 44v-12h10v-20h-10Z"
            fill={RH.steel}
            stroke={RH.black}
            strokeWidth="3"
            strokeLinejoin="round"
          />
        </g>
      </>,
    ),
  Products: [
    () =>
      svg(
        '0 0 100 100',
        <>
          <rect
            x="45"
            y="36"
            width="10"
            height="52"
            rx="2"
            fill="#cfcbff"
            stroke={RH.black}
            strokeWidth="2"
          />
          <path d="M24 22h40l14 6v6H64v6H24Z" fill={RH.black} />
        </>,
      ),
    () =>
      svg(
        '0 0 100 100',
        <>
          <path d="M30 26q20-16 40 0" fill="none" stroke={RH.black} strokeWidth="3" />
          <rect
            x="26"
            y="30"
            width="48"
            height="56"
            rx="5"
            fill={RH.yellow}
            stroke={RH.black}
            strokeWidth="3"
          />
          <rect x="26" y="46" width="48" height="18" fill={RH.black} />
          <path d="M40 30v12a4 4 0 0 0 8 0v-6a3 3 0 0 0 6 0v-6" fill="#5b57f2" />
        </>,
      ),
    () =>
      svg(
        '0 0 100 100',
        <>
          <polygon points={hexagon(50, 24, 16)} fill={RH.black} />
          <rect
            x="43"
            y="36"
            width="14"
            height="50"
            fill={RH.steel}
            stroke={RH.black}
            strokeWidth="2"
          />
          {[44, 52, 60, 68, 76].map((y) => (
            <path key={y} d={`M43 ${y}l14-4`} stroke={RH.black} strokeWidth="1.6" />
          ))}
        </>,
      ),
  ],
}

// ---------------------------------------------------------------------------------------
// Toko Batik Melati: indigo and teak, kawung batik
// ---------------------------------------------------------------------------------------

const BA = {
  indigo: '#1f2a5c',
  deep: '#131a3a',
  teak: '#5b57f2',
  soga: '#3a33c2',
  cream: '#fff1d6',
}

const kawung = ({
  key,
  id,
  fg = BA.cream,
  bg = BA.indigo,
}: {
  key?: Key

  id: string
  fg?: string
  bg?: string
}) => (
  <pattern key={key} id={id} width="20" height="20" patternUnits="userSpaceOnUse">
    <rect width="20" height="20" fill={bg} />
    <g fill={fg}>
      <ellipse cx="10" cy="3.6" rx="3.4" ry="5.6" />
      <ellipse cx="10" cy="16.4" rx="3.4" ry="5.6" />
      <ellipse cx="3.6" cy="10" rx="5.6" ry="3.4" />
      <ellipse cx="16.4" cy="10" rx="5.6" ry="3.4" />
    </g>
    <circle cx="10" cy="10" r="1.3" fill={BA.teak} />
    <circle cx="0" cy="0" r="1.3" fill={BA.teak} />
    <circle cx="20" cy="20" r="1.3" fill={BA.teak} />
  </pattern>
)

const parang = ({ key, id }: { key?: Key; id: string }) => (
  <pattern
    key={key}
    id={id}
    width="12"
    height="12"
    patternUnits="userSpaceOnUse"
    patternTransform="rotate(-45)"
  >
    <rect width="12" height="12" fill={BA.soga} />
    <path d="M0 6q3-5 6 0t6 0" fill="none" stroke={BA.cream} strokeWidth="1.6" />
  </pattern>
)

const melati: SiteArt = {
  theme: 'melati',
  Logo: () =>
    svg(
      '0 0 24 24',
      <g fill={BA.indigo}>
        <ellipse cx="12" cy="5.5" rx="3.6" ry="5" />
        <ellipse cx="12" cy="18.5" rx="3.6" ry="5" />
        <ellipse cx="5.5" cy="12" rx="5" ry="3.6" />
        <ellipse cx="18.5" cy="12" rx="5" ry="3.6" />
      </g>,
    ),
  Ornament: ({ uid }) =>
    svg(
      '0 0 400 14',
      <>
        <defs>{parang({ id: `${uid}pr` })}</defs>
        <rect width="400" height="14" fill={`url(#${uid}pr)`} />
      </>,
    ),
  Hero: ({ uid }) =>
    svg(
      '0 0 200 220',
      <>
        <defs>
          {kawung({ id: `${uid}kw` })}
          {parang({ id: `${uid}pr2` })}
        </defs>
        <rect x="26" y="8" width="148" height="204" rx="3" fill={`url(#${uid}kw)`} />
        <rect x="26" y="8" width="148" height="20" fill={`url(#${uid}pr2)`} />
        <rect x="26" y="192" width="148" height="20" fill={`url(#${uid}pr2)`} />
        <rect
          x="26"
          y="8"
          width="148"
          height="204"
          rx="3"
          fill="none"
          stroke={BA.deep}
          strokeWidth="2"
        />
        <g transform="rotate(-28 160 150)">
          <rect x="150" y="104" width="7" height="70" rx="3" fill="#cfcbff" stroke={BA.soga} />
          <path d="M144 100h19l-3 10h-13Z" fill="#5b57f2" stroke={BA.soga} />
          <path d="M163 103l14-6" stroke="#5b57f2" strokeWidth="2.4" strokeLinecap="round" />
        </g>
      </>,
    ),
  Products: [
    ({ uid }) =>
      svg(
        '0 0 100 100',
        <>
          <defs>
            {kawung({ id: `${uid}p1` })}
            {parang({ id: `${uid}p1b` })}
          </defs>
          <rect x="16" y="58" width="68" height="20" rx="3" fill={`url(#${uid}p1b)`} />
          <rect x="20" y="40" width="64" height="20" rx="3" fill="#fff1d6" stroke={BA.teak} />
          <rect x="14" y="22" width="70" height="20" rx="3" fill={`url(#${uid}p1)`} />
        </>,
      ),
    ({ uid }) =>
      svg(
        '0 0 100 100',
        <>
          <defs>{kawung({ id: `${uid}p2` })}</defs>
          <path
            d="M36 16l8-2 6 8 6-8 8 2 18 14-8 12-6-5v47H32V37l-6 5-8-12Z"
            fill={`url(#${uid}p2)`}
            stroke={BA.deep}
            strokeWidth="1.4"
            strokeLinejoin="round"
          />
          <path d="M44 14l6 8 6-8" fill={BA.cream} stroke={BA.deep} strokeWidth="1.2" />
        </>,
      ),
    ({ uid }) =>
      svg(
        '0 0 100 100',
        <>
          <defs>{parang({ id: `${uid}p3` })}</defs>
          <path d="M18 24c20-8 40 8 64-2v16c-24 10-44-6-64 2Z" fill={`url(#${uid}p3)`} />
          <path d="M18 46c20-8 40 8 64-2v16c-24 10-44-6-64 2Z" fill={BA.teak} />
          <path
            d="M18 68c20-8 40 8 64-2"
            fill="none"
            stroke={BA.soga}
            strokeWidth="1.4"
            strokeDasharray="1 3"
          />
        </>,
      ),
  ],
}

// ---------------------------------------------------------------------------------------
// Mama Nkechi's Kitchen: warm green and orange, bold and joyful
// ---------------------------------------------------------------------------------------

const MN = {
  green: '#1f7a3d',
  deep: '#0e4a24',
  orange: '#5b57f2',
  yellow: '#cfcbff',
  cream: '#fafafc',
  brown: '#3a33c2',
}

const grains = (cx: number, cy: number, r: number, count: number) =>
  Array.from({ length: count }, (_, i) => {
    const a = i * 2.39996
    const d = Math.sqrt((i + 0.5) / count) * r
    return (
      <ellipse
        key={i}
        cx={cx + Math.cos(a) * d}
        cy={cy + Math.sin(a) * d * 0.8}
        rx="1.9"
        ry="0.9"
        fill="#e9ddff"
        transform={`rotate(${(i * 47) % 180} ${cx + Math.cos(a) * d} ${cy + Math.sin(a) * d * 0.8})`}
      />
    )
  })

const puff = ({ key, x, y, r }: { key?: Key; x: number; y: number; r: number }) => (
  <g key={key}>
    <circle cx={x} cy={y} r={r} fill="#5b57f2" />
    <circle cx={x - r * 0.3} cy={y - r * 0.35} r={r * 0.4} fill="#cfcbff" />
  </g>
)

const nkechi: SiteArt = {
  theme: 'nkechi',
  Logo: () =>
    svg(
      '0 0 24 24',
      <>
        <path d="M4 10h16v3a8 8 0 0 1-16 0Z" fill={MN.yellow} />
        <path d="M2 11h3M19 11h3" stroke={MN.yellow} strokeWidth="2" strokeLinecap="round" />
        <path
          d="M9 7q1-2 0-4M13 7q1-2 0-4"
          stroke={MN.cream}
          strokeWidth="1.4"
          fill="none"
          strokeLinecap="round"
        />
      </>,
    ),
  Ornament: () =>
    svg(
      '0 0 200 200',
      <g fill="none" strokeWidth="7">
        <circle cx="100" cy="100" r="92" stroke={MN.deep} />
        <circle
          cx="100"
          cy="100"
          r="76"
          stroke={MN.yellow}
          strokeDasharray="2 14"
          strokeLinecap="round"
        />
      </g>,
    ),
  Hero: () =>
    svg(
      '0 0 220 200',
      <>
        <circle cx="110" cy="108" r="84" fill={MN.orange} />
        <circle cx="110" cy="108" r="72" fill="#fafafc" />
        <circle cx="110" cy="108" r="62" fill={MN.cream} />
        <path
          d="M64 104c0-24 22-40 46-38 26 1 46 16 44 40-2 22-24 34-46 32-24-2-44-12-44-34Z"
          fill="#5b57f2"
        />
        {grains(108, 102, 40, 70)}
        {[
          [146, 128, -30],
          [132, 140, -10],
          [116, 146, 12],
        ].map(([x, y, r]) => (
          <g key={x} transform={`rotate(${r} ${x} ${y})`}>
            <ellipse
              cx={x}
              cy={y}
              rx="11"
              ry="6.5"
              fill={MN.yellow}
              stroke="#5b57f2"
              strokeWidth="1.4"
            />
          </g>
        ))}
        <path
          d="M72 74c8-10 24-8 26 4 2 9-8 14-16 12l-10 10-6-6 10-10c-4-2-6-6-4-10Z"
          fill={MN.brown}
        />
        <circle cx="64" cy="102" r="4" fill="#fff1d6" />
        {[0, 1, 2].map((i) => (
          <path
            key={i}
            d={`M${92 + i * 18} 30q-6-8 0-14t0-14`}
            fill="none"
            stroke={MN.cream}
            strokeWidth="2.6"
            strokeLinecap="round"
            opacity="0.85"
          />
        ))}
        <path d="M150 84q8-6 12 2-8 4-12-2ZM158 96q8-2 9 6-8 1-9-6Z" fill="#52b05f" />
      </>,
    ),
  Products: [
    () =>
      svg(
        '0 0 100 100',
        <>
          <path d="M30 48c0-14 10-22 20-22s20 8 20 22Z" fill="#5b57f2" />
          {grains(50, 40, 14, 18)}
          <path
            d="M16 48h68c-2 20-16 32-34 32S18 68 16 48Z"
            fill="#fafafc"
            stroke={MN.green}
            strokeWidth="3"
          />
          <path d="M30 60h40" stroke={MN.orange} strokeWidth="3" strokeLinecap="round" />
        </>,
      ),
    () =>
      svg(
        '0 0 100 100',
        <>
          <path
            d="M18 72l20-40 20 40Z"
            fill="#cfcbff"
            stroke="#5b57f2"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <rect
            x="54"
            y="30"
            width="16"
            height="44"
            rx="8"
            fill="#5b57f2"
            transform="rotate(18 62 52)"
          />
          {puff({ x: 76, y: 70, r: 10 })}
        </>,
      ),
    () =>
      svg(
        '0 0 100 100',
        <>
          {puff({ x: 36, y: 62, r: 15 })}
          {puff({ x: 64, y: 62, r: 15 })}
          {puff({ x: 50, y: 40, r: 15 })}
          {[30, 46, 58, 70, 52].map((x, i) => (
            <circle key={i} cx={x} cy={30 + ((i * 17) % 40)} r="1.3" fill="#fafafc" />
          ))}
        </>,
      ),
  ],
}

// ---------------------------------------------------------------------------------------
// Amani Fresh: fresh greens, soft rounded shapes
// ---------------------------------------------------------------------------------------

const AM = {
  green: '#2e9a58',
  leaf: '#1e6a3c',
  lime: '#cfcbff',
  mango: '#cfcbff',
  cream: '#fafafc',
  seed: '#3a33c2',
  tomato: '#cfcbff',
}

const leaf = ({
  key,
  x,
  y,
  r,
  s = 1,
  fill = AM.green,
}: {
  key?: Key

  x: number
  y: number
  r: number
  s?: number
  fill?: string
}) => (
  <g key={key} transform={`translate(${x} ${y}) rotate(${r}) scale(${s})`}>
    <path d="M0 0C6-10 20-12 30-6 22 4 10 8 0 0Z" fill={fill} />
    <path
      d="M2 -1C10-5 18-6 26-6"
      fill="none"
      stroke="#ffffff"
      strokeOpacity="0.5"
      strokeWidth="0.8"
    />
  </g>
)

const avocado = ({ key, x, y, s = 1 }: { key?: Key; x: number; y: number; s?: number }) => (
  <g key={key} transform={`translate(${x} ${y}) scale(${s})`}>
    <path
      d="M0-26c10 0 16 12 18 26 2 14-6 24-18 24S-20 14-18 0C-16-14-10-26 0-26Z"
      fill="#3e6b2a"
    />
    <path d="M0-21c8 0 12 10 14 21 1 11-5 19-14 19S-15 11-14 0C-12-11-8-21 0-21Z" fill="#e9ddff" />
    <circle cx="0" cy="6" r="8" fill={AM.seed} />
    <circle cx="-2.5" cy="3.5" r="2.4" fill="#5b57f2" />
  </g>
)

const mango = ({
  key,
  x,
  y,
  r = 0,
  s = 1,
}: {
  key?: Key
  x: number
  y: number
  r?: number
  s?: number
}) => (
  <g key={key} transform={`translate(${x} ${y}) rotate(${r}) scale(${s})`}>
    <ellipse cx="0" cy="0" rx="24" ry="17" fill={AM.mango} />
    <ellipse cx="9" cy="-4" rx="13" ry="10" fill="#cfcbff" opacity="0.55" />
    <ellipse cx="-8" cy="-6" rx="6" ry="3" fill="#fff" opacity="0.35" />
    {leaf({ x: 20, y: -10, r: -30, s: 0.5, fill: AM.leaf })}
  </g>
)

const amani: SiteArt = {
  theme: 'amani',
  Logo: () =>
    svg(
      '0 0 24 24',
      <>
        <circle cx="12" cy="12" r="11" fill={AM.green} />
        <path d="M7 15c2-6 7-9 11-8-1 5-5 9-11 8Z" fill={AM.cream} />
      </>,
    ),
  Ornament: () =>
    svg(
      '0 0 200 200',
      <path
        d="M40 60c24-40 92-48 126-14 30 30 28 92-6 118-36 28-104 26-128-10-20-30-12-64 8-94Z"
        fill="#e3efcf"
      />,
    ),
  Hero: () =>
    svg(
      '0 0 220 200',
      <>
        {leaf({ x: 36, y: 104, r: -150, s: 1.3, fill: AM.leaf })}
        {leaf({ x: 182, y: 100, r: -20, s: 1.3 })}
        {mango({ x: 78, y: 98, r: -18, s: 1.1 })}
        {avocado({ x: 122, y: 94, s: 1.15 })}
        <circle cx="160" cy="106" r="17" fill={AM.tomato} />
        <circle cx="154" cy="100" r="4" fill="#fff" opacity="0.35" />
        <path
          d="M155 90q5-5 10 0"
          fill="none"
          stroke={AM.leaf}
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path d="M24 112h172c-4 50-38 78-86 78S28 162 24 112Z" fill="#cfcbff" />
        {[128, 146, 164].map((y) => (
          <path
            key={y}
            d={`M${30 + (y - 112) * 0.25} ${y}h${160 - (y - 112) * 0.5}`}
            stroke="#5b57f2"
            strokeWidth="2"
            strokeDasharray="10 5"
          />
        ))}
        <rect x="20" y="106" width="180" height="12" rx="6" fill="#5b57f2" />
      </>,
    ),
  Products: [
    () => svg('0 0 100 100', mango({ x: 50, y: 54, r: -20, s: 1.3 })),
    () => svg('0 0 100 100', avocado({ x: 50, y: 54, s: 1.2 })),
    () =>
      svg(
        '0 0 100 100',
        <>
          {leaf({ x: 48, y: 82, r: -100, s: 1.8, fill: AM.leaf })}
          {leaf({ x: 50, y: 82, r: -70, s: 1.8 })}
          {leaf({ x: 52, y: 82, r: -125, s: 1.6, fill: '#3f9b4f' })}
          <rect x="42" y="74" width="16" height="7" rx="3" fill={AM.lime} />
        </>,
      ),
  ],
}

// ---------------------------------------------------------------------------------------
// Padaria Estrela: azulejo blue tiles, warm bread
// ---------------------------------------------------------------------------------------

const PE = {
  blue: '#1d4f9f',
  deep: '#0f2c68',
  white: '#f8fafe',
  crust: '#5b57f2',
  top: '#cfcbff',
  yolk: '#cfcbff',
}

const azulejo = ({ key, id }: { key?: Key; id: string }) => (
  <pattern key={key} id={id} width="24" height="24" patternUnits="userSpaceOnUse">
    <rect width="24" height="24" fill={PE.white} />
    <g fill={PE.blue}>
      <circle cx="8" cy="12" r="4.4" />
      <circle cx="16" cy="12" r="4.4" />
      <circle cx="12" cy="8" r="4.4" />
      <circle cx="12" cy="16" r="4.4" />
      <circle cx="0" cy="0" r="5.5" />
      <circle cx="24" cy="0" r="5.5" />
      <circle cx="0" cy="24" r="5.5" />
      <circle cx="24" cy="24" r="5.5" />
      <path d="M12 0l2 2-2 2-2-2ZM12 20l2 2-2 2-2-2ZM0 12l2-2 2 2-2 2ZM20 12l2-2 2 2-2 2Z" />
    </g>
    <circle cx="12" cy="12" r="2.6" fill={PE.white} />
    <g fill={PE.white}>
      <circle cx="0" cy="0" r="2.4" />
      <circle cx="24" cy="0" r="2.4" />
      <circle cx="0" cy="24" r="2.4" />
      <circle cx="24" cy="24" r="2.4" />
    </g>
  </pattern>
)

const roll = ({
  key,
  x,
  y,
  r = 0,
  s = 1,
}: {
  key?: Key
  x: number
  y: number
  r?: number
  s?: number
}) => (
  <g key={key} transform={`translate(${x} ${y}) rotate(${r}) scale(${s})`}>
    <ellipse cx="0" cy="0" rx="26" ry="13" fill={PE.crust} />
    <ellipse cx="-2" cy="-3" rx="21" ry="8" fill={PE.top} />
    <path
      d="M-14 -4q14-6 26 0"
      fill="none"
      stroke="#5b57f2"
      strokeWidth="2.2"
      strokeLinecap="round"
    />
  </g>
)

const queijo = ({ key, x, y, r }: { key?: Key; x: number; y: number; r: number }) => (
  <g key={key}>
    <circle cx={x} cy={y} r={r} fill="#e9ddff" />
    <circle cx={x - r * 0.3} cy={y - r * 0.35} r={r * 0.35} fill="#fff1d6" />
    <circle cx={x + r * 0.4} cy={y + r * 0.2} r={r * 0.12} fill="#5b57f2" />
  </g>
)

const estrela: SiteArt = {
  theme: 'estrela',
  Logo: ({ uid }) =>
    svg(
      '0 0 24 24',
      <>
        <defs>{azulejo({ id: `${uid}lg` })}</defs>
        <circle cx="12" cy="12" r="11" fill={`url(#${uid}lg)`} stroke={PE.blue} strokeWidth="1.5" />
      </>,
    ),
  Ornament: ({ uid }) =>
    svg(
      '0 0 400 24',
      <>
        <defs>{azulejo({ id: `${uid}tl` })}</defs>
        <rect width="400" height="24" fill={`url(#${uid}tl)`} />
      </>,
    ),
  Hero: ({ uid }) =>
    svg(
      '0 0 220 200',
      <>
        <defs>{azulejo({ id: `${uid}hb` })}</defs>
        <rect x="10" y="10" width="200" height="180" rx="6" fill={`url(#${uid}hb)`} />
        <rect
          x="10"
          y="10"
          width="200"
          height="180"
          rx="6"
          fill="none"
          stroke={PE.deep}
          strokeWidth="3"
        />
        <circle cx="110" cy="112" r="74" fill={PE.white} />
        <path
          d="M58 76h30v10a15 15 0 0 1-30 0Z"
          fill={PE.white}
          stroke={PE.blue}
          strokeWidth="2.4"
        />
        <path d="M88 80h4a5 5 0 0 1 0 10h-4" fill="none" stroke={PE.blue} strokeWidth="2.4" />
        <ellipse cx="73" cy="101" rx="22" ry="4" fill={PE.blue} />
        <path
          d="M68 70q-4-6 0-12M78 70q-4-6 0-12"
          fill="none"
          stroke={PE.blue}
          strokeWidth="1.6"
          strokeLinecap="round"
          opacity="0.6"
        />
        <path d="M54 132h112c-4 26-26 40-56 40s-52-14-56-40Z" fill="#cfcbff" />
        <path d="M60 146h100M66 158h88" stroke="#5b57f2" strokeWidth="2" strokeDasharray="8 4" />
        {roll({ x: 92, y: 124, r: -12 })}
        {roll({ x: 128, y: 118, r: 10, s: 0.95 })}
        {queijo({ x: 150, y: 130, r: 9 })}
        {queijo({ x: 74, y: 134, r: 8 })}
      </>,
    ),
  Products: [
    () => svg('0 0 100 100', roll({ x: 50, y: 54, r: -14, s: 1.4 })),
    () =>
      svg(
        '0 0 100 100',
        <>
          {queijo({ x: 36, y: 60, r: 15 })}
          {queijo({ x: 64, y: 60, r: 15 })}
          {queijo({ x: 50, y: 38, r: 14 })}
        </>,
      ),
    () =>
      svg(
        '0 0 100 100',
        <>
          <circle cx="50" cy="54" r="28" fill={PE.crust} />
          <circle cx="44" cy="46" r="18" fill={PE.top} />
          <path
            d="M24 60q26 12 52 0"
            fill="none"
            stroke={PE.yolk}
            strokeWidth="6"
            strokeLinecap="round"
          />
          {[36, 46, 56, 64, 40].map((x, i) => (
            <circle key={i} cx={x} cy={36 + ((i * 13) % 20)} r="1.4" fill="#fff" />
          ))}
        </>,
      ),
  ],
}

// ---------------------------------------------------------------------------------------
// Taquería El Güero: rosa mexicano and green, papel picado
// ---------------------------------------------------------------------------------------

const TQ = {
  rosa: '#e4007c',
  verde: '#00a651',
  yellow: '#cfcbff',
  cream: '#fafafc',
  ink: '#2e3446',
  pastor: '#cfcbff',
}

const flagPath = (x: number) => {
  let d = `M${x} 8H${x + 46}V52`
  for (let i = 0; i < 4; i++) d += `L${x + 46 - i * 11.5 - 5.75} 58L${x + 46 - (i + 1) * 11.5} 52`
  return `${d}Z`
}

const taco = ({
  key,
  x,
  y,
  s = 1,
  filling = TQ.pastor,
}: {
  key?: Key

  x: number
  y: number
  s?: number
  filling?: string
}) => (
  <g key={key} transform={`translate(${x} ${y}) scale(${s})`}>
    <path d="M-30 4a30 26 0 0 1 60 0Z" fill={filling} />
    {[-16, -6, 4, 14].map((dx, i) => (
      <rect
        key={i}
        x={dx}
        y={-12 + (i % 2) * 5}
        width="5"
        height="5"
        rx="1"
        fill={i % 2 ? '#fafafc' : TQ.yellow}
      />
    ))}
    {[-12, 0, 10, 20].map((dx, i) => (
      <circle key={i} cx={dx} cy={-6 - (i % 2) * 6} r="2.2" fill="#2e8b3d" />
    ))}
    <path
      d="M-34 6a34 30 0 0 1 68 0c0 4-3 6-6 6h-56c-3 0-6-2-6-6Z"
      fill="#e9ddff"
      stroke="#cfcbff"
      strokeWidth="1.6"
    />
  </g>
)

const guero: SiteArt = {
  theme: 'guero',
  Logo: () =>
    svg(
      '0 0 24 24',
      <>
        <path d="M2 16a10 9 0 0 1 20 0Z" fill={TQ.pastor} />
        <path d="M1 17a11 10 0 0 1 22 0c0 1-1 2-2 2H3c-1 0-2-1-2-2Z" fill={TQ.yellow} />
      </>,
    ),
  Ornament: () =>
    svg(
      '0 0 400 60',
      <>
        <path d="M0 8Q200 14 400 8" fill="none" stroke={TQ.ink} strokeWidth="1.2" />
        {Array.from({ length: 8 }, (_, i) => {
          const x = 4 + i * 50
          const color = [TQ.rosa, TQ.verde, TQ.yellow, '#cfcbff'][i % 4]
          return (
            <g key={i}>
              <path d={flagPath(x)} fill={color} />
              <g fill={TQ.cream}>
                <circle cx={x + 23} cy={26} r="6" />
                <circle cx={x + 23} cy={26} r="2" fill={color} />
                {[10, 36].map((dx) => (
                  <path key={dx} d={`M${x + dx} 20l3 3-3 3-3-3Z`} />
                ))}
                {[12, 23, 34].map((dx) => (
                  <circle key={dx} cx={x + dx} cy={42} r="2.2" />
                ))}
              </g>
            </g>
          )
        })}
      </>,
    ),
  Hero: () =>
    svg(
      '0 0 200 220',
      <>
        <path d="M100 6v196" stroke="#8d8f94" strokeWidth="3" />
        <ellipse cx="100" cy="40" rx="16" ry="14" fill={TQ.yellow} />
        <path
          d="M92 28q-6-14 4-22M100 26V6M108 28q6-14-4-22"
          fill="none"
          stroke="#2e8b3d"
          strokeWidth="4"
          strokeLinecap="round"
        />
        <path d="M62 56h76l-10 112H72Z" fill={TQ.pastor} />
        {[70, 86, 102, 118, 134, 150].map((y, i) => (
          <path
            key={y}
            d={`M${64 + i * 1.6} ${y}h${72 - i * 3.2}`}
            stroke="#5b57f2"
            strokeWidth="3"
            strokeLinecap="round"
          />
        ))}
        <path d="M66 168h68l-4 10H70Z" fill="#5b57f2" />
        {taco({ x: 142, y: 186, s: 1.1 })}
        <path d="M30 186a14 14 0 0 1 28 0Z" fill="#7cc242" stroke="#3d8b2a" strokeWidth="2" />
      </>,
    ),
  Products: [
    () => svg('0 0 100 100', taco({ x: 50, y: 62, s: 1.25 })),
    () => svg('0 0 100 100', taco({ x: 50, y: 62, s: 1.25, filling: '#3a33c2' })),
    () => svg('0 0 100 100', taco({ x: 50, y: 62, s: 1.25, filling: '#5b57f2' })),
  ],
}

/** Every concept site's art, by project id. */
export const SITE_ART: Readonly<Record<ShowcaseProjectId, SiteArt>> = {
  'sharma-sweets': sharma,
  'noor-chikan': noor,
  'raipur-hardware': raipur,
  'toko-melati': melati,
  'mama-nkechi': nkechi,
  'amani-fresh': amani,
  'padaria-estrela': estrela,
  'el-guero': guero,
}
